import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Mic, MicOff, Square, Play, Pause, Trash2, Pencil,
  Sparkles, Clock, Tag, AlertCircle, Send, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { todayLocal } from '../utils/dateUtils';

const VoiceJournal = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchEntries();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/voice-journal?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching voice entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        // For now, show manual entry option since Whisper needs configuration
        setShowManualEntry(true);
        toast.info('Recording complete! Please enter or edit your transcript below.');
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
      setShowManualEntry(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const analyzeAndSave = async () => {
    if (!transcript.trim()) {
      toast.error('Please enter some text to analyze');
      return;
    }

    setIsAnalyzing(true);
    try {
      const today = todayLocal();
      const response = await axios.post(`${API}/voice-journal/analyze`, {
        date: today,
        transcript: transcript.trim(),
        duration_seconds: recordingTime || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Journal entry saved and analyzed!');
      setTranscript('');
      setRecordingTime(0);
      setShowManualEntry(false);
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveWithoutAnalysis = async () => {
    if (!transcript.trim()) {
      toast.error('Please enter some text');
      return;
    }

    try {
      const today = todayLocal();
      await axios.post(`${API}/voice-journal`, {
        date: today,
        transcript: transcript.trim(),
        duration_seconds: recordingTime || null,
        ai_summary: null,
        ai_categories: [],
        mood_detected: null,
        symptoms_detected: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Journal entry saved!');
      setTranscript('');
      setRecordingTime(0);
      setShowManualEntry(false);
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this journal entry?')) return;
    
    try {
      await axios.delete(`${API}/voice-journal/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditText(entry.transcript || '');
  };

  const saveEdit = async (entryId) => {
    if (!editText.trim()) {
      toast.error('Entry cannot be empty');
      return;
    }
    try {
      await axios.put(`${API}/voice-journal/${entryId}`, { transcript: editText.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry updated');
      setEditingId(null);
      setEditText('');
      fetchEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodEmoji = (mood) => {
    const moods = {
      positive: '😊',
      neutral: '😐',
      anxious: '😰',
      stressed: '😓',
      hopeful: '🌟',
      exhausted: '😴',
      frustrated: '😤'
    };
    return moods[mood] || '📝';
  };

  return (
    <div className="space-y-8" data-testid="voice-journal">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-xl p-8">
        <div className="flex items-center gap-4 mb-2">
          <Mic className="w-10 h-10 text-violet-600" strokeWidth={1.5} />
          <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
            Voice Journal
          </h1>
        </div>
        <p className="text-[#57534E]">
          Speak your observations and let AI transcribe and analyze them for patterns
        </p>
      </div>

      {/* Recording Section */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#2D2D2D] mb-4">Record New Entry</h2>
        
        {!showManualEntry ? (
          <div className="text-center py-8">
            {/* Recording Button */}
            <div className="mb-6">
              {!isRecording ? (
                <Button
                  onClick={startRecording}
                  className="w-24 h-24 rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="start-recording-btn"
                >
                  <Mic className="w-10 h-10" />
                </Button>
              ) : (
                <Button
                  onClick={stopRecording}
                  className="w-24 h-24 rounded-full bg-red-500 hover:bg-red-600 text-white animate-pulse"
                  data-testid="stop-recording-btn"
                >
                  <Square className="w-10 h-10" />
                </Button>
              )}
            </div>
            
            {/* Recording Time */}
            {isRecording && (
              <div className="flex items-center justify-center gap-2 text-xl font-mono text-red-500 mb-4">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                {formatTime(recordingTime)}
              </div>
            )}
            
            <p className="text-[#57534E]">
              {isRecording 
                ? 'Recording... Tap to stop when done.' 
                : 'Tap the microphone to start recording'}
            </p>
            
            {/* Manual Entry Option */}
            <Button
              onClick={() => setShowManualEntry(true)}
              variant="ghost"
              className="mt-4 text-violet-600"
              data-testid="manual-entry-btn"
            >
              Or type your entry manually
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-[#2D2D2D] font-medium">Your Journal Entry</Label>
              <Textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Describe what you observed today... symptoms, behaviors, mood, progress, concerns..."
                className="mt-2 min-h-[150px]"
                data-testid="transcript-input"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={analyzeAndSave}
                disabled={isAnalyzing || !transcript.trim()}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="analyze-save-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Save with AI Analysis
                  </>
                )}
              </Button>
              
              <Button
                onClick={saveWithoutAnalysis}
                disabled={!transcript.trim()}
                variant="outline"
                className="flex-1"
                data-testid="save-only-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Save Without Analysis
              </Button>
            </div>
            
            <Button
              onClick={() => {
                setShowManualEntry(false);
                setTranscript('');
              }}
              variant="ghost"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        )}
      </Card>

      {/* Info Card */}
      <Card className="p-4 bg-violet-50 border-violet-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-violet-600 mt-0.5" />
          <div>
            <p className="font-semibold text-[#2D2D2D]">AI-Powered Analysis</p>
            <p className="text-sm text-[#57534E]">
              When you save with AI analysis, we'll automatically detect mood, symptoms mentioned, 
              and categorize your entry for better tracking and correlation insights.
            </p>
          </div>
        </div>
      </Card>

      {/* Entries List */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2D2D2D]">Recent Entries</h2>
        
        {loading ? (
          <p className="text-center text-[#57534E] py-8">Loading...</p>
        ) : entries.length === 0 ? (
          <Card className="p-8 text-center">
            <Mic className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-[#57534E]">No voice journal entries yet. Record your first entry!</p>
          </Card>
        ) : (
          entries.map((entry) => (
            <Card key={entry.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`voice-entry-${entry.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getMoodEmoji(entry.mood_detected)}</span>
                  <div>
                    <p className="font-semibold text-[#2D2D2D]">{formatDate(entry.created_at)}</p>
                    {entry.duration_seconds && (
                      <p className="text-sm text-[#A8A29E] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(entry.duration_seconds)}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(entry)}
                    className="text-violet-600 hover:text-violet-700"
                    data-testid={`edit-voice-entry-${entry.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(entry.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Transcript */}
              {editingId === entry.id ? (
                <div className="space-y-3 mb-3">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="min-h-[120px]"
                    data-testid={`edit-voice-textarea-${entry.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => saveEdit(entry.id)}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                      data-testid={`save-voice-edit-${entry.id}`}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setEditingId(null); setEditText(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-[#57534E] mb-3 whitespace-pre-wrap">{entry.transcript}</p>
              )}
              
              {/* AI Summary */}
              {entry.ai_summary && (
                <div className="bg-violet-50 rounded-lg p-3 mb-3">
                  <p className="text-sm font-medium text-violet-700 flex items-center gap-1 mb-1">
                    <Sparkles className="w-3 h-3" />
                    AI Summary
                  </p>
                  <p className="text-sm text-[#57534E]">{entry.ai_summary}</p>
                </div>
              )}
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {entry.mood_detected && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full capitalize">
                    Mood: {entry.mood_detected}
                  </span>
                )}
                
                {entry.ai_categories && entry.ai_categories.map(cat => (
                  <span key={cat} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-full flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {cat.replace('_', ' ')}
                  </span>
                ))}
                
                {entry.symptoms_detected && entry.symptoms_detected.map(symptom => (
                  <span key={symptom} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                    {symptom}
                  </span>
                ))}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default VoiceJournal;
